import { position, autoUpdate } from '../floating.js';
import { onOutsidePointer } from '../listbox.js';

const normalizeHex = (value) => {
    let hex = String(value ?? '').trim().toLowerCase();
    if (!hex) return '';
    if (!hex.startsWith('#')) hex = `#${hex}`;
    if (/^#[0-9a-f]{3}$/.test(hex)) hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
    return /^#[0-9a-f]{6}$/.test(hex) ? hex : null;
};

/**
 * <mellow-color-picker>: swatches (a radio group), a hex field and the native color input,
 * in a popover. `.value` is "#rrggbb" (or "") for wire:model.
 */
export class MellowColorPicker extends HTMLElement {
    connectedCallback() {
        if (this._ready) return this.render();
        this._ready = true;

        this._value = normalizeHex(this.getAttribute('value')) ?? '';

        // Inner inputs must not reach wire:model on this element.
        this.addEventListener('input', this.onInner, true);
        this.addEventListener('change', this.onInner, true);

        this.trigger?.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
        this.panel?.addEventListener('keydown', this.onKeydown);
        this.panel?.addEventListener('click', (event) => {
            const swatch = event.target.closest('[data-color]');
            if (swatch) this.choose(swatch.dataset.color, true);
        });
        this.panel?.addEventListener('toggle', (event) => {
            if (event.newState === 'closed') {
                this.trigger?.setAttribute('aria-expanded', 'false');
                this._cleanup?.();
                this._outside?.();
            }
        });

        this.querySelector('[data-mellow-color-clear]')?.addEventListener('click', () => {
            this.choose('', false);
            this.trigger?.focus();
        });

        this.render();
    }

    get trigger() {
        return this.querySelector('.mellow-color-trigger');
    }

    get panel() {
        return this.querySelector(':scope > [popover]');
    }

    get swatches() {
        return [...this.querySelectorAll('[data-color]')];
    }

    get isOpen() {
        return this.panel?.matches(':popover-open') ?? false;
    }

    get value() {
        return this._value ?? '';
    }

    set value(value) {
        this._value = normalizeHex(value) ?? '';
        this.render();
    }

    /** Livewire morphs reset the server-rendered trigger: render again. */
    sync() {
        this.render();
    }

    choose(value, close) {
        const hex = normalizeHex(value);
        if (hex === null) return;

        this._value = hex;
        this.render();
        this.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: hex }));
        this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: hex }));
        if (close) this.close();
    }

    onInner = (event) => {
        if (event.target === this) return;
        event.stopPropagation();

        if (event.target.matches('[data-mellow-color-native]')) {
            this.choose(event.target.value, false);
        } else if (event.target.matches('[data-mellow-color-hex]') && event.type === 'change') {
            // Commit typed hex codes on change (Enter / blur), not on every keystroke.
            const hex = normalizeHex(event.target.value);
            event.target.toggleAttribute('aria-invalid', hex === null);
            if (hex !== null) this.choose(hex, false);
        }
    };

    render() {
        const value = this.value;

        const swatch = this.querySelector('.mellow-color-trigger .mellow-color-swatch');
        if (swatch) {
            swatch.style.setProperty('--_color', value || 'transparent');
            swatch.toggleAttribute('data-empty', !value);
        }

        const display = this.querySelector('.mellow-listbox-value');
        const text = value || this.dataset.placeholder || '';
        if (display && display.textContent !== text) display.textContent = text;
        display?.toggleAttribute('data-empty', !value);

        const clear = this.querySelector('[data-mellow-color-clear]');
        if (clear) clear.hidden = !value;

        // Roving tabindex: the selected swatch (or the first) is the tab stop of the group.
        const swatches = this.swatches;
        const checked = swatches.find((s) => s.dataset.color === value);
        swatches.forEach((s) => {
            s.setAttribute('aria-checked', String(s === checked));
            s.tabIndex = s === (checked ?? swatches[0]) ? 0 : -1;
        });

        const native = this.querySelector('[data-mellow-color-native]');
        if (native && value && native.value !== value) native.value = value;
        const hex = this.querySelector('[data-mellow-color-hex]');
        if (hex && document.activeElement !== hex && hex.value !== value) hex.value = value;

        const hidden = this.querySelector('[data-mellow-color-value]');
        if (hidden && hidden.value !== value) hidden.value = value;
    }

    open() {
        if (this.isOpen || this.trigger?.disabled) return;
        this.panel.showPopover();
        this.trigger.setAttribute('aria-expanded', 'true');
        this._cleanup = autoUpdate(this.trigger, this.panel, () => position(this.trigger, this.panel, { placement: 'bottom', align: 'start', offset: 4 }));
        this._outside = onOutsidePointer(this, () => this.close(false));
        this.swatches.find((s) => s.tabIndex === 0)?.focus();
    }

    close(focusTrigger = true) {
        if (!this.isOpen) return;
        this.panel.hidePopover();
        if (focusTrigger) this.trigger?.focus();
    }

    onKeydown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            return this.close();
        }

        // Arrow keys move through the swatch grid (WAI-ARIA radio group).
        const swatch = event.target.closest('[data-color]');
        if (!swatch) return;

        const swatches = this.swatches;
        const index = swatches.indexOf(swatch);
        const columns = Math.max(1, Math.round(this.querySelector('.mellow-color-swatches').clientWidth / swatch.offsetWidth));
        const rtl = getComputedStyle(this).direction === 'rtl';

        const target = {
            ArrowRight: index + (rtl ? -1 : 1),
            ArrowLeft: index + (rtl ? 1 : -1),
            ArrowDown: index + columns,
            ArrowUp: index - columns,
            Home: 0,
            End: swatches.length - 1,
        }[event.key];

        if (target !== undefined) {
            event.preventDefault();
            const next = swatches[(target + swatches.length) % swatches.length];
            swatches.forEach((s) => (s.tabIndex = s === next ? 0 : -1));
            next.focus();
        } else if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            this.choose(swatch.dataset.color, true);
        }
    };
}
